import { Alert, Checkbox, Loader, Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faMinus, faPlus, faUtensils, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { DigitalMenuOptionGroup, DigitalMenuProductDetail } from 'shared-models';
import { Button } from '../../../components/Button';
import { menuMoney, type DigitalMenuBasketLine } from '../digital-menu.utils';
import { useDishConfiguration } from '../useDishConfiguration';
import classes from '../digital-menu.module.scss';

type Props = { opened: boolean; product: DigitalMenuProductDetail | null; loading: boolean; error: string; onClose: () => void; onAdd: (line: DigitalMenuBasketLine) => void };

export function ProductSheet({ opened, product, loading, error, onClose, onAdd }: Props) {
  return <Modal.Root opened={opened} onClose={onClose} size="100%" xOffset={0} yOffset={0} classNames={{ inner: classes.productModalInner, content: `${classes.modal} ${classes.productModal}` }}>
    <Modal.Overlay />
    <Modal.Content aria-label={product?.name ?? 'פרטי מנה'}>
    <Modal.Header className={classes.productHeader}>
      <Button variant="icon" ariaLabel="סגירת פרטי מנה" className={classes.close} onClick={onClose}><FontAwesomeIcon icon={faXmark as IconProp} /></Button>
    </Modal.Header>
    <Modal.Body className={classes.modalBody}>
    <div dir="rtl" className={classes.sheet}>
      {loading && <div className={classes.status} role="status"><Loader color="gray" /><span>טוען מנה...</span></div>}
      {error && <p className={classes.status} role="alert">{error}</p>}
      {product && <Dish key={product.id} product={product} onAdd={onAdd} />}
    </div>
    </Modal.Body>
    </Modal.Content>
  </Modal.Root>;
}

function Dish({ product, onAdd }: { product: DigitalMenuProductDetail; onAdd: Props['onAdd'] }) {
  const state = useDishConfiguration(product, onAdd);
  return <>
    <div className={classes.dishImage}>
      {product.imageUrl ? <img src={product.imageUrl} alt={product.name} /> : <FontAwesomeIcon icon={faUtensils as IconProp} />}
    </div>
    <div className={classes.dishContent}>
      <h2>{product.name}</h2><strong className={classes.price}>{menuMoney(product.price)}</strong>
      {product.description && <p className={classes.description}>{product.description}</p>}
      {product.tags.length > 0 && <div className={classes.tags}>{product.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
      {product.ingredients.length > 0 && <section className={classes.ingredients} aria-label="מרכיבים"><h3>מרכיבים</h3><p className={classes.description}>{product.ingredients.join(', ')}</p></section>}
      {product.allergens.length > 0 && <Alert color="yellow" title="אלרגנים" className={classes.allergens}>{product.allergens.join(' · ')}</Alert>}
      <OptionGroups groups={product.groups} state={state} />
    </div>
    <div className={classes.sheetFooter}>
      <Button variant="confirm" className={classes.addButton} disabled={!state.valid} onClick={state.add}>
        <span>{product.available ? 'הוסף לסל' : 'אזל מהמלאי'}</span><span>{menuMoney(state.line.unitPrice * state.quantity)}</span>
      </Button>
      <div className={classes.quantity}>
        <Button variant="icon" ariaLabel="הגדלת כמות מנה" disabled={state.quantity >= 99} onClick={() => state.setQuantity((value) => Math.min(99, value + 1))}><FontAwesomeIcon icon={faPlus as IconProp} /></Button>
        <output aria-label="כמות מנה">{state.quantity}</output>
        <Button variant="icon" ariaLabel="הקטנת כמות מנה" disabled={state.quantity <= 1} onClick={() => state.setQuantity((value) => Math.max(1, value - 1))}><FontAwesomeIcon icon={faMinus as IconProp} /></Button>
      </div>
    </div>
  </>;
}

function OptionGroups({ groups, state }: { groups: DigitalMenuOptionGroup[]; state: ReturnType<typeof useDishConfiguration> }) {
  return <>{groups.map((group) => {
    const total = group.items.reduce((sum, item) => sum + (state.counts[item.id] ?? 0), 0);
    return <fieldset className={classes.optionGroup} key={group.id}>
      <legend>{group.name}{group.minPick > 0 && <span className={classes.required}>חובה</span>}</legend>
      <p className={classes.optionMeta}>{group.pickLimit === 1 ? 'בחירה אחת' : `עד ${group.pickLimit} בחירות`}{group.minPick > 1 ? ` · לפחות ${group.minPick}` : ''}</p>
      {group.items.map((item) => {
        const selected = state.counts[item.id] ?? 0;
        const single = group.pickLimit === 1 || item.maxPick === 1;
        const atCap = total >= group.pickLimit || selected >= (item.maxPick ?? group.pickLimit);
        const extra = total < group.freeLimit ? item.priceWithinFreeLimit : item.priceAfterFreeLimit;
        return <div key={item.id}>
          <div className={classes.optionRow}>
            <Checkbox label={item.name} checked={selected > 0} disabled={!item.inStock || item.maxPick === 0 || (!selected && atCap && group.pickLimit !== 1)}
              onChange={() => state.pick(group, item.id, selected ? -1 : 1)} color="teal" />
            {!item.inStock ? <span className={classes.muted}>אזל מהמלאי</span> : <span className={classes.optionPrice}>{extra > 0 ? `+ ${menuMoney(extra)}` : ''}</span>}
            {!single && <div className={classes.optionQuantity}>
              <Button variant="icon" ariaLabel={`הוסף ${item.name}`} disabled={!item.inStock || atCap} onClick={() => state.pick(group, item.id, 1)}><FontAwesomeIcon icon={faPlus as IconProp} /></Button>
              <output>{selected}</output>
              <Button variant="icon" ariaLabel={`הסר ${item.name}`} disabled={!selected} onClick={() => state.pick(group, item.id, -1)}><FontAwesomeIcon icon={faMinus as IconProp} /></Button>
            </div>}
          </div>
          {selected > 0 && item.childGroups.length > 0 && <div className={classes.nested}><OptionGroups groups={item.childGroups} state={state} /></div>}
        </div>;
      })}
    </fieldset>;
  })}</>;
}